import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

import { routeToTransactionPage } from 'support/helpers/navigation/navigation';
import {
  ImportDocumentItemDTO,
  ImportDocumentsResponseDTO,
  ImportDocumentStatus,
  TransactionInternalType,
} from 'support/types';
import { Badge, BadgeColor } from 'components/Display/Badge/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';

export const ImportResults = ({ result }: { result: ImportDocumentsResponseDTO }) => {
  const { t } = useTranslation();
  const { items } = result;

  return (
    <div className="flex flex-col gap-4">
      {items.length > 1 && (
        <p className="text-sm text-gray-500">
          {t('transactions:list.importDocuments.processed', { count: items.length })}
        </p>
      )}

      <ul className="flex max-h-75 flex-col divide-y divide-gray-100 overflow-y-auto">
        {items.map((item, index) => (
          <li
            key={`${item.document_number}-${index}`}
            className="flex items-center justify-between gap-4 py-3 pr-2 text-sm"
          >
            <DocumentNumber item={item} />
            <div className="flex flex-none items-center gap-1.5">
              <Badge color={STATUS_BADGE_COLOR[item.status]}>{t(getStatusLabelKey(item))}</Badge>
              <StatusDetailTooltip item={item} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const DocumentNumber = ({ item }: { item: ImportDocumentItemDTO }) => {
  const className = 'whitespace-nowrap font-semibold text-gray-900';

  if (!item.sender_envelope_id) {
    return <span className={className}>{item.document_number}</span>;
  }

  return (
    <Link
      className={`${className} hover:text-procuros-green-600`}
      to={routeToTransactionPage(TransactionInternalType.senderEnvelope, item.sender_envelope_id)}
      target="_blank"
      rel="noopener noreferrer"
    >
      {item.document_number}
    </Link>
  );
};

const StatusDetailTooltip = ({ item }: { item: ImportDocumentItemDTO }) => {
  const diagnosis = item.diagnosis;

  if (!diagnosis) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger>
        <InformationCircleIcon className="size-4 text-gray-400 hover:text-procuros-green-500" />
      </TooltipTrigger>
      <TooltipContent>{diagnosis.translated_message}</TooltipContent>
    </Tooltip>
  );
};

const STATUS_BADGE_COLOR: Record<ImportDocumentStatus, BadgeColor> = {
  [ImportDocumentStatus.imported]: BadgeColor.green,
  [ImportDocumentStatus.duplicateSkipped]: BadgeColor.gray,
  [ImportDocumentStatus.notFound]: BadgeColor.yellow,
  [ImportDocumentStatus.failed]: BadgeColor.red,
};

/**
 * STATUS_LABEL_KEY and DIAGNOSIS_LABEL_KEY are consumed dynamically — the i18next parser can't
 * follow them, so the expansions are enumerated here.
 *
 * i18n keys
 * t('transactions:list.importDocuments.status.imported')
 * t('transactions:list.importDocuments.status.duplicate')
 * t('transactions:list.importDocuments.status.notFound')
 * t('transactions:list.importDocuments.status.failed')
 * t('transactions:list.importDocuments.status.retrievalFailed')
 * t('transactions:list.importDocuments.status.processingFailed')
 */
const STATUS_LABEL_KEY: Record<ImportDocumentStatus, string> = {
  [ImportDocumentStatus.imported]: 'transactions:list.importDocuments.status.imported',
  [ImportDocumentStatus.duplicateSkipped]: 'transactions:list.importDocuments.status.duplicate',
  [ImportDocumentStatus.notFound]: 'transactions:list.importDocuments.status.notFound',
  [ImportDocumentStatus.failed]: 'transactions:list.importDocuments.status.failed',
};

type ImportDocumentDiagnosisCode = NonNullable<ImportDocumentItemDTO['diagnosis']>['code'];

const DIAGNOSIS_LABEL_KEY: Partial<Record<ImportDocumentDiagnosisCode, string>> = {
  DOCUMENT_NOT_FOUND: 'transactions:list.importDocuments.status.notFound',
  SOURCE_RETRIEVAL_FAILED: 'transactions:list.importDocuments.status.retrievalFailed',
  PROCESSING_FAILED: 'transactions:list.importDocuments.status.processingFailed',
};

const getStatusLabelKey = (item: ImportDocumentItemDTO): string =>
  (item.diagnosis && DIAGNOSIS_LABEL_KEY[item.diagnosis.code]) ?? STATUS_LABEL_KEY[item.status];
